import * as XLSX from "xlsx";

const TR_MONTHS = {
  ocak: 1, şubat: 2, subat: 2, mart: 3, nisan: 4, mayıs: 5, mayis: 5,
  haziran: 6, temmuz: 7, ağustos: 8, agustos: 8, eylül: 9, eylul: 9,
  ekim: 10, kasım: 11, kasim: 11, aralık: 12, aralik: 12,
};

const normalizeHeaderKey = (raw) => String(raw ?? "")
  .replace(/\u2192/g, " ")
  .replace(/\n/g, " ")
  .replace(/\s+/g, " ")
  .trim()
  .toLocaleLowerCase("tr-TR");

const HEADER_TO_FIELD = [
  { match: (h) => h.includes("asistan"), field: null },
  { match: (h) => h === "takım lideri" || h === "takim lideri", field: "_excel_team_leader" },
  { match: (h) => h === "çalışma süresi" || h === "calisma suresi", field: "_excel_work_duration" },
  { match: (h) => h.includes("tüm atanan data"), field: "total_assigned_data" },
  { match: (h) => h.includes("tüm dataların işlem") || h.includes("tum datalarin islem"), field: "total_processed_patients" },
  { match: (h) => h.includes("tüm data işlem oranı") || h.includes("tum data islem"), field: "total_data_process_rate" },
  { match: (h) => h.includes("wp toplam"), field: "wp_total_data" },
  { match: (h) => h.includes("wp dataların işlem") || h.includes("wp datalarin islem"), field: "wp_processed_count" },
  { match: (h) => h.includes("wp dönüşüm") || h.includes("wp donusum"), field: "wp_conversion_rate" },
  { match: (h) => h.includes("kaliteli atanan"), field: "kaliteli_assigned_data" },
  { match: (h) => h.includes("kaliteli data işlem sayısı") || h.includes("kaliteli data islem sayisi"), field: "kaliteli_processed_count" },
  { match: (h) => h.includes("kaliteli dataların işlem") || h.includes("kaliteli datalarin islem"), field: "kaliteli_process_rate" },
  { match: (h) => h.includes("randevu iptal"), field: "randevu_iptal" },
  { match: (h) => h === "randevu" || h.startsWith("randevu "), field: "randevu" },
  { match: (h) => h === "işlem" || h === "islem", field: "islem" },
  { match: (h) => h === "muayene", field: "muayene" },
  { match: (h) => (h.includes("dış arama oranı") || h.includes("dis arama orani")) && !h.includes("atama"), field: "dis_arama_conversion_rate" },
  { match: (h) => h.includes("dış arama atama") || h.includes("dis arama atama"), field: "dis_arama_planlanan" },
  { match: (h) => h.includes("aldığı dış") || h.includes("aldigi dis"), field: "dis_arama_alinan" },
  { match: (h) => h.includes("planlanan dış") || h.includes("planlanan dis"), field: "dis_arama_planlanan" },
];

const mapHeaderToField = (header) => {
  const h = normalizeHeaderKey(header);
  if (!h) return null;
  for (const rule of HEADER_TO_FIELD) {
    if (rule.match(h)) {
      if (rule.field === null) return "assistant_name";
      return rule.field;
    }
  }
  return null;
};

export const parseKaliteliSheetPeriodTitle = (title) => {
  const t = String(title ?? "").trim();
  const m = t.match(/(\d{1,2})\s*-\s*(\d{1,2})\s+([^\d]+?)\s+(\d{4})/i);
  if (!m) return null;
  const dayStart = parseInt(m[1], 10);
  const dayEnd = parseInt(m[2], 10);
  const monthKey = m[3].trim().toLocaleLowerCase("tr-TR");
  const year = parseInt(m[4], 10);
  const month = TR_MONTHS[monthKey];
  if (!month || !year) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return {
    period_start: `${year}-${pad(month)}-${pad(dayStart)}`,
    period_end: `${year}-${pad(month)}-${pad(dayEnd)}`,
  };
};

const cellValue = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const findHeaderRowIndex = (rows) => {
  for (let i = 0; i < Math.min(rows.length, 15); i += 1) {
    const line = (rows[i] || []).map((c) => normalizeHeaderKey(c)).join(" ");
    if (line.includes("asistan") && (line.includes("wp") || line.includes("kaliteli"))) {
      return i;
    }
  }
  return -1;
};

const extractPreparedDate = (rows) => {
  for (let i = 0; i < Math.min(rows.length, 5); i += 1) {
    for (const cell of rows[i] || []) {
      const s = String(cell ?? "");
      const m = s.match(/Haz[ıi]rlama Tarihi\s*:\s*(\d{1,2}\.\d{1,2}\.\d{4})/i);
      if (m) return m[1];
    }
  }
  return null;
};

const parseSheetRows = (sheetRows) => {
  const headerIdx = findHeaderRowIndex(sheetRows);
  if (headerIdx < 0) return { rows: [], prepared_at: extractPreparedDate(sheetRows) };

  const headerRow = sheetRows[headerIdx] || [];
  const colMap = {};
  headerRow.forEach((h, colIdx) => {
    const field = mapHeaderToField(h);
    if (field && field !== "_excel_team_leader" && field !== "_excel_work_duration") {
      colMap[colIdx] = field;
    }
  });

  const out = [];
  for (let r = headerIdx + 1; r < sheetRows.length; r += 1) {
    const row = sheetRows[r] || [];
    const record = {};
    let hasAssistant = false;
    Object.entries(colMap).forEach(([colIdx, field]) => {
      const val = cellValue(row[Number(colIdx)]);
      if (val === null) return;
      record[field] = val;
      if (field === "assistant_name") hasAssistant = true;
    });
    if (hasAssistant && record.assistant_name) {
      out.push(record);
    }
  }

  return { rows: out, prepared_at: extractPreparedDate(sheetRows) };
};

export const parseKaliteliDataWorkbook = (arrayBuffer) => {
  const wb = XLSX.read(arrayBuffer, { type: "array", cellDates: false });
  const sheets = [];

  wb.SheetNames.forEach((sheetName) => {
    const ws = wb.Sheets[sheetName];
    const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
    const period = parseKaliteliSheetPeriodTitle(sheetName)
      || parseKaliteliSheetPeriodTitle((matrix[0] || []).find((c) => c && String(c).match(/\d+\s*-\s*\d+/)) || sheetName);
    if (!period) return;

    const { rows, prepared_at } = parseSheetRows(matrix);
    if (rows.length === 0) return;

    sheets.push({
      sheet_title: sheetName,
      period_start: period.period_start,
      period_end: period.period_end,
      prepared_at,
      rows,
    });
  });

  return sheets;
};

export const KALITELI_DATA_META_FIELDS = [
  { key: "leader_name", label: "Takım Lideri" },
  { key: "work_duration", label: "Çalışma Süresi" },
  { key: "assistant_name", label: "Asistanın adı" },
];

/** Kaliteli Data sekmesi — satır satır grid düzeni */
export const KALITELI_DATA_TAB_ROWS = [
  {
    rowClass: "grid grid-cols-1",
    fields: [{ key: "total_assigned_data", label: "Toplam Data Sayısı" }],
  },
  {
    rowClass: "grid grid-cols-1 sm:grid-cols-3",
    fields: [
      { key: "wp_total_data", label: "WP toplam data sayısı" },
      { key: "wp_processed_count", label: "WP datalarından işlem sayısı" },
      { key: "wp_conversion_rate", label: "WP dönüşüm işlem oranı" },
    ],
  },
  {
    rowClass: "grid grid-cols-1 sm:grid-cols-3",
    fields: [
      { key: "kaliteli_assigned_data", label: "Kaliteli atanan data sayısı" },
      { key: "kaliteli_processed_count", label: "Kaliteli data işlem sayısı" },
      { key: "kaliteli_process_rate", label: "Kaliteli data işlem oranı" },
    ],
  },
  {
    rowClass: "grid grid-cols-2 sm:grid-cols-4",
    fields: [
      { key: "randevu", label: "Randevu sayısı" },
      { key: "randevu_iptal", label: "İptal" },
      { key: "islem", label: "İşlem" },
      { key: "muayene", label: "Muayene sayısı" },
    ],
  },
  {
    rowClass: "grid grid-cols-1 sm:grid-cols-2",
    fields: [
      { key: "dis_arama_alinan", label: "Aldığı dış arama" },
      { key: "dis_arama_planlanan", label: "Planlanan dış arama" },
    ],
  },
];

export const KALITELI_DATA_DISPLAY_FIELDS = [
  ...KALITELI_DATA_META_FIELDS,
  ...KALITELI_DATA_TAB_ROWS.flatMap((r) => r.fields),
];

export const normalizeAgentNameForMatch = (name) => {
  let s = String(name ?? "").trim().toLocaleUpperCase("tr-TR");
  s = s.replace(/\s+/g, " ");
  const trMap = { İ: "I", İ: "I", Ş: "S", Ğ: "G", Ü: "U", Ö: "O", Ç: "C" };
  return s.split("").map((c) => trMap[c] ?? c).join("");
};

/** Ad soyad varyasyonları (sıra, ilk+son) — Excel ↔ panel eşleştirme */
export const nameMatchKeys = (name) => {
  const norm = normalizeAgentNameForMatch(name);
  const keys = new Set();
  if (!norm) return keys;
  keys.add(norm);
  const parts = norm.split(" ").filter(Boolean);
  if (parts.length >= 2) {
    keys.add(`${parts[0]} ${parts[parts.length - 1]}`);
    keys.add(`${parts[parts.length - 1]} ${parts[0]}`);
    if (parts.length > 2) {
      keys.add(parts.slice(0, 2).join(" "));
      keys.add(parts.slice(-2).join(" "));
    }
  }
  return keys;
};

export const buildAgentNameLookup = (agentList) => {
  const lookup = new Map();
  (agentList || []).forEach((a) => {
    const id = a.id;
    const label = a.ad_soyad || a.name || "";
    nameMatchKeys(label).forEach((key) => {
      if (!lookup.has(key)) lookup.set(key, { id, ad_soyad: label });
    });
  });
  return lookup;
};

export const resolveAgentForExcelName = (excelName, lookup) => {
  const keys = nameMatchKeys(excelName);
  for (const key of keys) {
    if (lookup.has(key)) return lookup.get(key);
  }
  return null;
};

export const filterSheetRowsWithAgentMatch = (sheet, lookup, manualMap = {}) => {
  const byName = new Map();
  (sheet.rows || []).forEach((row) => {
    const excelName = String(row.assistant_name || "").trim();
    if (!excelName) return;
    if (manualMap[excelName]?.id) {
      byName.set(excelName, row);
      return;
    }
    if (resolveAgentForExcelName(excelName, lookup)) {
      byName.set(excelName, row);
    }
  });
  return { ...sheet, rows: Array.from(byName.values()) };
};

/** Excel sheet listesi + firma agentları ile eşleşme önizlemesi */
export const buildKaliteliImportPreview = (sheets, agentList) => {
  const lookup = buildAgentNameLookup(agentList);

  const sheetPreviews = (sheets || []).map((sheet, idx) => {
    const dedupMap = new Map();
    (sheet.rows || []).forEach((r) => {
      const n = String(r.assistant_name || "").trim();
      if (n) dedupMap.set(n, r);
    });
    const rows = Array.from(dedupMap.values());
    const names = [...dedupMap.keys()];
    const unmatchedNames = [];
    let matchedRowCount = 0;
    rows.forEach((row) => {
      const name = String(row.assistant_name || "").trim();
      if (!name) return;
      if (resolveAgentForExcelName(name, lookup)) {
        matchedRowCount += 1;
      }
    });
    names.forEach((name) => {
      if (!resolveAgentForExcelName(name, lookup)) unmatchedNames.push(name);
    });
    unmatchedNames.sort((a, b) => a.localeCompare(b, "tr"));

    return {
      key: `${sheet.sheet_title}-${idx}`,
      sheet_title: sheet.sheet_title,
      period_start: sheet.period_start,
      period_end: sheet.period_end,
      prepared_at: sheet.prepared_at,
      row_count: rows.length,
      matched_row_count: matchedRowCount,
      assistant_count: names.length,
      matched_count: names.length - unmatchedNames.length,
      unmatched_names: unmatchedNames,
      selected: true,
      raw: sheet,
    };
  });

  const allUnmatched = [...new Set(sheetPreviews.flatMap((s) => s.unmatched_names))].sort((a, b) => a.localeCompare(b, "tr"));
  const totalRows = sheetPreviews.reduce((sum, s) => sum + s.row_count, 0);
  const totalMatchedRows = sheetPreviews.reduce((sum, s) => sum + s.matched_row_count, 0);

  return {
    sheetPreviews,
    allUnmatched,
    totalRows,
    totalMatchedRows,
    agentPoolSize: (agentList || []).length,
    totalAssistants: [...new Set(sheetPreviews.flatMap((s) => (s.raw.rows || []).map((r) => r.assistant_name).filter(Boolean)))].length,
  };
};

export const formatKaliteliRate = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string" && value.includes("%")) return value;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (n > 0 && n <= 1) return `${(n * 100).toFixed(2).replace(/\.?0+$/, "")}%`;
  return String(value);
};
